import { FC } from 'react';
import { Divider, Tabs, Typography } from 'antd';
import { FileLock } from 'lucide-react';
import { TbArticle, TbCloudLock, TbCloudUpload } from 'react-icons/tb';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { FlexContentRegion, FullHeightFlexColumn } from '@/shared';

import { AuditLogs, AuditLogStreaming, DataRetention, SAMLSSO } from './components';
import Metadata from './meta';

// interface WorkspaceSecurityProps {}

const WorkspaceSecurity: FC = (): JSX.Element => {
    const navigate = useNavigate();
    const location = useLocation();
    const url = '/settings/workspace/security';

    const { token } = useAntDesignTheme();
    const activeKey = location.pathname.split('/')[4] ?? 'overview';

    const onChangeTab = (newActiveKey: string) => {
        navigate(`${url}/${newActiveKey}`);
    };

    const items = [
        {
            label: (
                <>
                    <TbCloudLock size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.375rem' }} />
                    Overview
                </>
            ),
            key: 'overview',
            children: <SAMLSSO />,
        },
        {
            label: (
                <>
                    <TbArticle size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.375rem' }} />
                    Audit Logs
                </>
            ),
            key: 'logs',
            children: <AuditLogs />,
        },
        {
            label: (
                <>
                    <TbCloudUpload size='1rem' style={{ verticalAlign: '-0.175rem', marginRight: '0.375rem' }} />
                    Event Streaming
                </>
            ),
            key: 'events',
            children: <AuditLogStreaming />,
        },
        {
            label: (
                <>
                    <FileLock
                        aria-hidden='true'
                        size='0.875rem'
                        strokeWidth={1.75}
                        style={{ verticalAlign: '-0.125rem', marginRight: '0.375rem' }}
                    />
                    Data Retention
                </>
            ),
            key: 'retention',
            children: <DataRetention />,
            // Data Retention owns its scrolling region so enforcement status,
            // policy actions, and documentation remain fixed together.
            style: {
                display: 'flex',
                flexDirection: 'column' as const,
                height: '100%',
                minHeight: 0,
                overflow: 'hidden' as const,
            },
        },
    ];

    return (
        <FullHeightFlexColumn>
            <Metadata />
            <style>
                {`
                    .workspace-security-tabs {
                        display: flex;
                        flex-direction: column;
                        height: 100%;
                        min-height: 0;
                    }

                    .workspace-security-tabs > .ant-tabs-nav {
                        flex: none;
                        margin-bottom: 0;
                    }

                    .workspace-security-tabs > .ant-tabs-content-holder {
                        flex: 1 1 0%;
                        min-height: 0;
                    }

                    .workspace-security-tabs > .ant-tabs-content-holder > .ant-tabs-content,
                    .workspace-security-tabs > .ant-tabs-content-holder > .ant-tabs-content > .ant-tabs-tabpane {
                        height: 100%;
                        min-height: 0;
                    }

                    .workspace-security-tabs > .ant-tabs-content-holder > .ant-tabs-content > .ant-tabs-tabpane {
                        box-sizing: border-box;
                        min-width: 0;
                        overflow-x: hidden;
                        overflow-y: auto;
                        padding-top: 1rem;
                    }
                `}
            </style>
            <Typography.Title style={{ flex: 'none' }}>Workspace Security</Typography.Title>
            <Typography.Paragraph style={{ color: token.colorTextTertiary, flex: 'none', marginBottom: 0 }}>
                Manage security, identity, audit, and data governance controls for your workspace.
            </Typography.Paragraph>
            <Divider style={{ flex: 'none', marginTop: '1rem', marginBottom: 0 }} />
            <FlexContentRegion>
                <Routes>
                    <Route index element={<Navigate to={`${url}/overview`} replace />} />
                    <Route
                        path='*'
                        element={
                            <Tabs
                                activeKey={activeKey}
                                animated={true}
                                className='workspace-security-tabs'
                                items={items}
                                size='small'
                                onChange={onChangeTab}
                            />
                        }
                    />
                </Routes>
            </FlexContentRegion>
        </FullHeightFlexColumn>
    );
};

export default WorkspaceSecurity;
